import { useEffect, useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { GoogleAddressInput } from "../GoogleAddressInput";
import { formatDateTime } from "../../lib/format";
import api from "../../lib/api";

export const MOBILIZZAZIONI = ["Autonomo", "Carrozzina", "Barella"];
export const EMPTY_SR = {
  paziente_nominativo: "", paziente_nato_il: "", paziente_peso: "", paziente_cf: "",
  struttura_partenza: "", partenza_indirizzo: "", reparto_partenza: "",
  destinazione_nome: "", destinazione_indirizzo: "", reparto_destinazione: "",
  data_ora_carico: "", orario_visita: "", mezzo: "", motivo: "", note_motivo: "", mobilizzazione: "",
  andata_ritorno: false, fermo_macchina: false, note: "",
};
export const pickSR = (d) => Object.fromEntries(Object.keys(EMPTY_SR).map((k) => [k, d?.[k] ?? EMPTY_SR[k]]));

const STATO_CLS = {
  ricevuta: "bg-amber-100 text-amber-800 border-amber-200",
  presa_in_carico: "bg-sky-100 text-sky-800 border-sky-200",
  assegnata: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const STATO_LABEL = { ricevuta: "Ricevuta", presa_in_carico: "Presa in carico", assegnata: "Assegnata" };
export const SRStatoBadge = ({ stato, testid }) => (
  <Badge data-testid={testid} variant="outline" className={`font-semibold ${STATO_CLS[stato] || ""}`}>{STATO_LABEL[stato] || stato}</Badge>
);

const F = ({ label, full, children }) => (
  <div className={full ? "md:col-span-2" : ""}><Label className="nx-label">{label}</Label>{children}</div>
);
const Sub = ({ children }) => <p className="md:col-span-2 nx-label text-primary border-b border-slate-200 pb-1 mt-2">{children}</p>;

const MobPicker = ({ value, onChange }) => (
  <div className="mt-1.5 grid grid-cols-3 gap-2">
    {MOBILIZZAZIONI.map((m) => (
      <button key={m} type="button" data-testid={`sr-mob-${m.toLowerCase()}`} onClick={() => onChange(m)}
        className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${value === m ? "border-primary bg-primary text-white" : "border-slate-200 bg-white text-slate-700 hover:border-primary/50"}`}>
        {m}
      </button>
    ))}
  </div>
);

const OptSelect = ({ value, onChange, items, testid, placeholder }) => {
  const list = value && !items.includes(value) ? [value, ...items] : items;
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="mt-1.5"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{list.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
};

const ToggleRow = ({ testid, checked, onChange, label }) => (
  <label className="mt-1.5 flex items-center gap-3 rounded-md border border-slate-200 bg-white px-3 py-2 cursor-pointer">
    <Switch data-testid={testid} checked={!!checked} onCheckedChange={onChange} />
    <span className={`text-sm ${checked ? "font-semibold text-primary" : "text-slate-700"}`}>{label}</span>
  </label>
);

const placeName = (p) => (p.text || p.indirizzo || "").split(",")[0].trim();

export const SRForm = ({ value, onChange, optionsUrl = "/portale/opzioni" }) => {
  const [opts, setOpts] = useState({ mezzo: [], causale: [] });
  useEffect(() => { api.get(optionsUrl).then(({ data }) => setOpts(data)).catch(() => {}); }, [optionsUrl]);
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const inp = (k, props = {}) => <Input data-testid={`sr-${k}`} value={value[k]} onChange={(e) => set(k)(e.target.value)} className="mt-1.5" {...props} />;
  const addr = (k, placeholder, onPick) => (
    <GoogleAddressInput testid={`sr-${k}`} value={value[k]} onChange={set(k)} placeholder={placeholder} onPick={onPick} />
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Sub>Paziente</Sub>
      <F label="Nominativo paziente *" full>{inp("paziente_nominativo")}</F>
      <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <F label="Data di nascita">{inp("paziente_nato_il", { type: "date" })}</F>
        <F label="Peso (kg)">{inp("paziente_peso", { inputMode: "decimal", placeholder: "es. 75" })}</F>
        <F label="Codice fiscale">{inp("paziente_cf", { maxLength: 16, className: "mt-1.5 uppercase" })}</F>
      </div>
      <Sub>Partenza</Sub>
      <F label="Struttura di partenza">{addr("struttura_partenza", "Cerca la struttura di partenza...", (p) => onChange({ ...value, struttura_partenza: placeName(p), partenza_indirizzo: p.indirizzo }))}</F>
      <F label="Reparto di partenza">{inp("reparto_partenza", { placeholder: "Es. Cardiologia, 3° piano" })}</F>
      <F label="Indirizzo di partenza" full>{addr("partenza_indirizzo", "Cerca l'indirizzo di partenza...", (p) => onChange({ ...value, partenza_indirizzo: p.indirizzo }))}</F>
      <Sub>Destinazione</Sub>
      <F label="Struttura di destinazione *">{addr("destinazione_nome", "Cerca la struttura di destinazione...", (p) => onChange({ ...value, destinazione_nome: placeName(p), destinazione_indirizzo: p.indirizzo }))}</F>
      <F label="Reparto di destinazione">{inp("reparto_destinazione", { placeholder: "Es. Radiologia" })}</F>
      <F label="Indirizzo di destinazione" full>{addr("destinazione_indirizzo", "Cerca l'indirizzo della destinazione...", (p) => onChange({ ...value, destinazione_indirizzo: p.indirizzo }))}</F>
      <Sub>Servizio</Sub>
      <F label="Data e ora di carico *">{inp("data_ora_carico", { type: "datetime-local" })}</F>
      <F label="Orario della visita">{inp("orario_visita", { type: "time" })}</F>
      <F label="Mezzo richiesto"><OptSelect value={value.mezzo} onChange={set("mezzo")} items={opts.mezzo} testid="sr-mezzo" placeholder="Seleziona il mezzo" /></F>
      <F label="Mobilizzazione *"><MobPicker value={value.mobilizzazione} onChange={set("mobilizzazione")} /></F>
      <F label="Motivo del trasporto"><OptSelect value={value.motivo} onChange={set("motivo")} items={opts.causale} testid="sr-motivo" placeholder="Seleziona il motivo" /></F>
      <F label="Note sul motivo">{inp("note_motivo", { placeholder: "Dettagli sul motivo" })}</F>
      <F label="Tratta"><ToggleRow testid="sr-andata_ritorno" checked={value.andata_ritorno} onChange={set("andata_ritorno")} label="Andata e ritorno" /></F>
      <F label="Fermo macchina"><ToggleRow testid="sr-fermo_macchina" checked={value.fermo_macchina} onChange={set("fermo_macchina")} label="Richiesto fermo macchina" /></F>
      <F label="Note" full>
        <Textarea data-testid="sr-note" rows={3} value={value.note} onChange={(e) => set("note")(e.target.value)} className="mt-1.5" />
      </F>
    </div>
  );
};

const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "");
const join = (...xs) => xs.filter(Boolean).join(" · ");
const ROWS = [
  ["Nominativo paziente", (d) => d.paziente_nominativo, true],
  ["Data di nascita", (d) => fmtDate(d.paziente_nato_il)], ["Peso", (d) => d.paziente_peso && `${d.paziente_peso} kg`],
  ["Codice fiscale", (d) => d.paziente_cf],
  ["Partenza", (d) => join(d.struttura_partenza, d.partenza_indirizzo), true], ["Reparto di partenza", (d) => d.reparto_partenza],
  ["Destinazione", (d) => join(d.destinazione_nome, d.destinazione_indirizzo), true], ["Reparto di destinazione", (d) => d.reparto_destinazione],
  ["Data e ora di carico", (d) => formatDateTime(d.data_ora_carico)], ["Orario della visita", (d) => d.orario_visita],
  ["Mezzo richiesto", (d) => d.mezzo], ["Mobilizzazione", (d) => d.mobilizzazione],
  ["Motivo del trasporto", (d) => d.motivo], ["Note sul motivo", (d) => d.note_motivo],
  ["Tratta", (d) => (d.andata_ritorno ? "Andata e ritorno" : "Solo andata")],
  ["Fermo macchina", (d) => (d.fermo_macchina ? "Richiesto" : "Non richiesto")], ["Note", (d) => d.note, true],
];

export const SRReadOnly = ({ data }) => (
  <dl data-testid="sr-readonly" className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
    {ROWS.map(([label, get, full]) => (
      <div key={label} className={full ? "md:col-span-2" : ""}>
        <dt className="nx-label">{label}</dt>
        <dd className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{get(data) || "—"}</dd>
      </div>
    ))}
  </dl>
);
